import React from 'react';
import { useAssetBuckets } from '../services/api';
import { DEFAULT_WINDOW, SIDES, METHOD_NOTE, GRID_NOTE, summarizeTilt, compareToPrevious, swingNote, windowGrid, barShare, AXIS_ENDS } from '../shared/bucketCrowding';
import { HIGHLIGHT_COLORS, GRID_INK, AXIS_INK, INK, MUTED, shortDate, fmtPct, Pill, LoadingNote } from './sectorChartShared';

/**
 * Crowded buckets (issue #369; plain-language pass #372): the 13 asset-class buckets ranked by how
 * unusually hard each was bought or sold over the chosen window, against its
 * own history, with a plain-English takeaway and how it compares with the period before.
 * Definition, windows and every word come from src/shared/bucketCrowding.js. It describes;
 * it makes no forward-looking statement (that file's header says why).
 *
 * Since issue #400 it is a GRID: one row per group, one column per window the payload carries
 * (1D 1W 1M 3M), each cell that window's bar and move, on one scale across every window — so a
 * row shows whether a lean is a one-day blip or has been building. The toggle no longer swaps
 * the chart; it picks which window orders the rows, owns the takeaway/tags/notes and is marked
 * as the column those words describe. Plain HTML, every cell's move printed as text, so nothing
 * rides on colour. Blue / orange, not green / red: bought-hard is not "good".
 */

const [INFLOW, OUTFLOW] = HIGHLIGHT_COLORS;
const Tag = ({ children, color, title }) => (
  <span
    title={title}
    style={{
      fontSize: '0.62rem', lineHeight: 1.5, padding: '0 6px', borderRadius: '999px', whiteSpace: 'nowrap',
      border: `1px solid ${color || '#d1d5db'}`, color: color ? INK : '#6b7280', background: '#fff'
    }}
  >
    {children}
  </span>
);

// A cell: one window's bar for one group. The track is centred on its own zero; blue grows right
// (bought), orange grows left (sold). `null` (no reading in that window) is a dash, never an
// empty bar that would read as "nothing happened".
const Cell = ({ cell, scale, chosen }) => {
  if (!cell) {
    return <div style={{ fontSize: '0.7rem', color: MUTED, textAlign: 'center' }}>—</div>;
  }
  const color = cell.z >= 0 ? INFLOW : OUTFLOW;
  const width = `${barShare(cell.z, scale) * 50}%`;
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ position: 'relative', height: 10 }} aria-hidden="true">
        <div style={{ position: 'absolute', left: '50%', top: -2, bottom: -2, width: 1, background: AXIS_INK }} />
        <div
          style={{
            position: 'absolute', top: 1, bottom: 1, width,
            [cell.z >= 0 ? 'left' : 'right']: '50%',
            background: color, opacity: chosen ? 1 : 0.55,
            borderRadius: cell.z >= 0 ? '0 3px 3px 0' : '3px 0 0 3px'
          }}
        />
      </div>
      <div style={{ fontSize: '0.66rem', textAlign: 'center', fontVariantNumeric: 'tabular-nums', color: chosen ? INK : '#6b7280', whiteSpace: 'nowrap', marginTop: 1 }}>
        {fmtPct(cell.movePct)}
      </div>
    </div>
  );
};

// One template for the header and every row: the label column, then one equal column per window.
const columnsFor = (windows) => `minmax(96px, min(30%, 260px)) repeat(${windows.length}, minmax(64px, 1fr))`;

const BucketRow = ({ row, windows, chosenKey, scale, note, columns }) => {
  const color = row.z >= 0 ? INFLOW : OUTFLOW;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: columns, alignItems: 'center', gap: '12px', padding: '4px 0' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '0.74rem', color: INK, fontWeight: row.callout ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.label} <span style={{ color: MUTED, fontWeight: 400 }}>{row.ticker}</span>
        </div>
        {(row.callout || note) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '1px', flexWrap: 'wrap' }}>
            {row.callout && <Tag color={color}>{SIDES[row.callout].tag}</Tag>}
            {note && <span style={{ fontSize: '0.64rem', color: '#6b7280', whiteSpace: 'nowrap' }}>{note}</span>}
          </div>
        )}
      </div>
      {windows.map((w) => <Cell key={w.key} cell={row.cells[w.key]} scale={scale} chosen={w.key === chosenKey} />)}
    </div>
  );
};

const AssetBucketsView = () => {
  const [windowKey, setWindowKey] = React.useState(DEFAULT_WINDOW);
  const { data, isLoading, isError } = useAssetBuckets();

  if (isLoading) {
    return <LoadingNote>Loading asset classes…</LoadingNote>;
  }
  // Every window the payload carries is a column (issue #400); a cached row from before a window
  // was added simply has one column fewer. The chosen window orders the rows and owns the words.
  const grid = windowGrid(data, windowKey);
  if (isError || !grid) return null;
  const { windows, chosen, rows, scale } = grid;
  const tilt = summarizeTilt(rows, chosen.period);
  const comparison = compareToPrevious(rows, chosen);
  const omitted = data.windows[chosen.key].omitted || [];
  const columns = columnsFor(windows);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
        <h4 style={{ fontSize: '0.85rem', fontWeight: 600, color: INK, margin: 0 }}>
          Where money has been moving
          <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '0.7rem' }}>
            {' '}· as of {shortDate(data.as_of)}{data.stale ? ' (not refreshed recently)' : ''}
          </span>
        </h4>
        <div style={{ display: 'flex', gap: '4px' }}>
          {windows.map((w) => (
            <Pill key={w.key} active={w.key === chosen.key} onClick={() => setWindowKey(w.key)}>{w.label}</Pill>
          ))}
        </div>
      </div>

      <div style={{ margin: '8px 0 2px', lineHeight: 1.5 }}>
        <div style={{ fontSize: '0.82rem', fontWeight: 600, color: INK }}>{tilt.headline}</div>
        <div style={{ fontSize: '0.72rem', color: '#4b5563' }}>{tilt.detail}</div>
        {comparison && <div style={{ fontSize: '0.72rem', color: '#4b5563' }}>{comparison}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: columns, gap: '12px', margin: '10px 0 2px', fontSize: '0.62rem', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', alignItems: 'end' }}>
        <span>Group</span>
        {windows.map((w) => (
          <span
            key={w.key}
            style={{ textAlign: 'center', whiteSpace: 'nowrap', color: w.key === chosen.key ? INK : MUTED, fontWeight: w.key === chosen.key ? 700 : 400, borderBottom: w.key === chosen.key ? `2px solid ${INK}` : '2px solid transparent', paddingBottom: 2 }}
          >
            {w.label}
          </span>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: columns, gap: '12px', fontSize: '0.6rem', color: MUTED, margin: '2px 0 4px' }}>
        <span />
        <span style={{ gridColumn: `2 / span ${windows.length}`, display: 'flex', justifyContent: 'space-between', whiteSpace: 'nowrap' }}>
          <span>← {AXIS_ENDS.sold}</span><span>{AXIS_ENDS.bought} →</span>
        </span>
      </div>
      <div style={{ borderTop: `1px solid ${GRID_INK}`, borderBottom: `1px solid ${GRID_INK}`, padding: '4px 0' }}>
        {rows.map((row) => (
          <BucketRow key={row.ticker} row={row} windows={windows} chosenKey={chosen.key} scale={scale} note={swingNote(row, chosen)} columns={columns} />
        ))}
      </div>

      <div style={{ fontSize: '0.65rem', color: MUTED, marginTop: '6px', lineHeight: 1.5 }}>
        {GRID_NOTE} The takeaway above and the tags describe {chosen.period} (the marked column). {METHOD_NOTE}
        {omitted.length > 0 && ` Unavailable right now: ${omitted.join(', ')}.`}
      </div>
    </div>
  );
};

export default AssetBucketsView;
